import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const toolVariants = cva(
  "group rounded-lg border transition-all duration-150 overflow-hidden",
  {
    variants: {
      status: {
        detected: "border-border/50 bg-muted/30",
        "input-streaming": "border-primary/30 bg-primary/5",
        "input-available": "border-primary/50 bg-primary/10",
        "output-streaming": "border-primary/50 bg-primary/10",
        "output-available": "border-success/50 bg-success/10",
        "output-error": "border-destructive/50 bg-destructive/10",
        loading: "border-border/50 bg-muted/30",
      },
    },
    defaultVariants: {
      status: "detected",
    },
  }
);

type ToolStatus =
  | "detected"
  | "input-streaming"
  | "input-available"
  | "output-streaming"
  | "output-available"
  | "output-error"
  | "loading";

export interface ToolProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof toolVariants> {
  name: string;
  status?: ToolStatus;
  isOpen?: boolean;
  onToggle?: () => void;
}

const Tool = React.forwardRef<HTMLDivElement, ToolProps>(
  (
    { className, status = "detected", name, isOpen = false, onToggle, children, ...props },
    ref
  ) => {
    const [internalOpen, setInternalOpen] = React.useState(isOpen);
    const open = isOpen !== undefined ? isOpen : internalOpen;

    const handleToggle = () => {
      if (onToggle) {
        onToggle();
      } else {
        setInternalOpen(!internalOpen);
      }
    };

    const statusConfig: Record<ToolStatus, { label: string; icon: React.ReactNode; color: string }> = {
      detected: {
        label: "已检测",
        icon: (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        ),
        color: "text-muted-foreground",
      },
      "input-streaming": {
        label: "接收参数...",
        icon: (
          <span className="relative flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-primary"></span>
          </span>
        ),
        color: "text-primary",
      },
      "input-available": {
        label: "执行中...",
        icon: (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ),
        color: "text-primary",
      },
      "output-streaming": {
        label: "接收结果...",
        icon: (
          <span className="relative flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-primary"></span>
          </span>
        ),
        color: "text-primary",
      },
      "output-available": {
        label: "已完成",
        icon: (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ),
        color: "text-success",
      },
      "output-error": {
        label: "执行失败",
        icon: (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ),
        color: "text-destructive",
      },
      loading: {
        label: "加载中...",
        icon: (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ),
        color: "text-muted-foreground",
      },
    };

    const config = statusConfig[status];

    return (
      <div ref={ref} className={cn(toolVariants({ status }), className)} {...props}>
        <button
          type="button"
          onClick={handleToggle}
          className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className={cn("flex items-center", config.color)}>{config.icon}</span>
            <span className="font-medium">{name}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn("text-xs", config.color)}>{config.label}</span>
            <svg
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-150",
                open && "rotate-180"
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        <div
          className={cn(
            "overflow-hidden transition-all duration-150 ease-in-out",
            open ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="border-t border-border/50">{children}</div>
        </div>
      </div>
    );
  }
);
Tool.displayName = "Tool";

// Tool Header Component
const ToolHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-2 px-4 py-2 text-xs font-medium text-muted-foreground", className)}
    {...props}
  >
    {children}
  </div>
));
ToolHeader.displayName = "ToolHeader";

// Tool Input Component
const ToolInput = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { data: unknown }
>(({ className, data, ...props }, ref) => (
  <div ref={ref} className={cn("px-4 py-3", className)} {...props}>
    <div className="mb-2 text-xs font-medium text-muted-foreground">参数</div>
    <pre className="rounded-md bg-muted/50 p-3 text-xs font-mono overflow-auto max-h-48">
      {JSON.stringify(data, null, 2)}
    </pre>
  </div>
));
ToolInput.displayName = "ToolInput";

// Tool Output Component
const ToolOutput = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { data: unknown; error?: string }
>(({ className, data, error, ...props }, ref) => (
  <div ref={ref} className={cn("px-4 py-3", className)} {...props}>
    <div className="mb-2 text-xs font-medium text-muted-foreground">结果</div>
    {error ? (
      <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
        {error}
      </div>
    ) : (
      <pre className="rounded-md bg-muted/50 p-3 text-xs font-mono overflow-auto max-h-48">
        {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
      </pre>
    )}
  </div>
));
ToolOutput.displayName = "ToolOutput";

type CardStatus = 'detected' | 'executing' | 'success' | 'failed';

const cardStatusVariants: Record<CardStatus, string> = {
  detected: "border-border/50 bg-muted/30",
  executing: "border-primary/50 bg-primary/10",
  success: "border-success/50 bg-success/10",
  failed: "border-destructive/50 bg-destructive/10",
};

const cardStatusConfig: Record<CardStatus, { label: string; icon: React.ReactNode; color: string }> = {
  detected: {
    label: "已检测",
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    color: "text-muted-foreground",
  },
  executing: {
    label: "执行中",
    icon: (
      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    ),
    color: "text-primary",
  },
  success: {
    label: "已完成",
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    color: "text-success",
  },
  failed: {
    label: "执行失败",
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    color: "text-destructive",
  },
};

interface ToolCardShellProps {
  toolName: string;
  status: CardStatus;
  icon?: React.ReactNode;
  iconColor?: string;
  defaultExpanded?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
  className?: string;
}

const ToolCardShell = React.forwardRef<HTMLDivElement, ToolCardShellProps>(
  (
    {
      toolName,
      status,
      icon,
      iconColor,
      defaultExpanded = false,
      isOpen: isOpenProp,
      onToggle,
      children,
      className,
    },
    ref
  ) => {
    const [internalOpen, setInternalOpen] = React.useState(defaultExpanded);
    const open = isOpenProp !== undefined ? isOpenProp : internalOpen;
    const hasChildren = React.Children.count(children) > 0;

    const handleToggle = () => {
      if (onToggle) {
        onToggle();
      } else {
        setInternalOpen((prev) => !prev);
      }
    };

    const config = cardStatusConfig[status] || cardStatusConfig.detected;
    const variantClass = cardStatusVariants[status] || cardStatusVariants.detected;

    return (
      <div
        ref={ref}
        className={cn(
          "group rounded-lg border transition-all duration-150 overflow-hidden",
          variantClass,
          className
        )}
      >
        <button
          type="button"
          onClick={hasChildren ? handleToggle : undefined}
          className={cn(
            "flex w-full items-center justify-between px-4 py-3 text-sm font-medium transition-colors",
            hasChildren && "hover:bg-muted/50"
          )}
        >
          <div className="flex items-center gap-3">
            {icon && (
              <span className={cn("flex items-center", iconColor)}>{icon}</span>
            )}
            <span className="font-medium">{toolName}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn("text-xs", config.color)}>{config.label}</span>
            {hasChildren && (
              <svg
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform duration-150",
                  open && "rotate-180"
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </div>
        </button>
        {hasChildren && (
          <div
            className={cn(
              "overflow-hidden transition-all duration-150 ease-in-out",
              open ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
            )}
          >
            <div className="border-t border-border/50">{children}</div>
          </div>
        )}
      </div>
    );
  }
);
ToolCardShell.displayName = "ToolCardShell";

export { Tool, ToolHeader, ToolInput, ToolOutput, ToolCardShell, toolVariants };
export type { ToolStatus, CardStatus, ToolCardShellProps };
